import { omit } from 'lodash';
import { useMemo } from 'react';

/**
 * Merges shipment events, POM request events, and notes into a single
 * chronologically-sorted timeline that the HistoryPanel can render.
 */
const useTimelineItems = ({ events, requestEvents, comments, order, patient }) =>
  useMemo(() => {
    const items = [
      ...events.map((e) => {
        const isShipstationShipped =
          e.auditEventType === 'ORDER_SHIPPED' && e.shipmentSource === 'SHIPSTATION';
        return {
          kind: 'event',
          isShipmentError: e.isShipmentError,
          timestamp: e.auditEventData?.timestamp || e.createdTimestamp,
          shipstationShippedDate:
            isShipstationShipped && e.auditEventData?.shippedDate
              ? e.auditEventData.shippedDate
              : null,
          label: e.auditEventType?.replace(/_/g, ' ') || '',
          detail: e.auditEventData?.performedByName || '',
          rawEvent: e,
        };
      }),
      ...requestEvents.map((e) => {
        const isShipstationShipped = e.actionItemType === 'SHIPSTATION_ORDER_SHIPPED';
        return {
          kind: 'request',
          id: e.id,
          isProcessSuccess: e.isProcessSuccess,
          timestamp: e.timestamp,
          shipstationShippedDate:
            isShipstationShipped && e.interactionData?.shipDate ? e.interactionData.shipDate : null,
          label: e.actionItemType?.replace(/_/g, ' ') || '',
          detail: e.user ? `${e.user.firstName} ${e.user.lastName}` : '',
          interactionData: e.interactionData,
          eventPayload: omit(e.interactionData || {}, ['originalEventPayload']),
          requestDetails: {
            firstName: patient?.firstName,
            lastName: patient?.lastName,
            actionItemType: e.actionItemType,
            orderId: order?.orderId,
            requestId: e.interactionData?.requestId,
            rxNumber: e.interactionData?.rxNumber,
            referenceId: e.interactionData?.referenceId,
            fromPharmacyNpi: e.interactionData?.transferFrom?.npi,
            toPharmacyNpi: e.interactionData?.transferTo?.npi,
            user: e.user,
          },
        };
      }),
      ...comments.map((c) => ({
        kind: 'note',
        noteId: c.noteId,
        timestamp: c.noteCreatedAt,
        label: c.noteContent || '',
        detail: c.noteAuthor ? `${c.noteAuthor.firstName} ${c.noteAuthor.lastName}` : '',
        isShippingRelated: c.isShippingRelated ?? false,
      })),
    ];

    return items.sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return ta - tb;
    });
  }, [events, requestEvents, comments, order, patient]);

export default useTimelineItems;
